import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Send, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 }, whileInView: { opacity: 1, y: 0 },
  viewport: { once: true }, transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) return toast.error("Please fill in all fields.");
    setSending(true);
    try {
      await api.post("/contact", form);
      toast.success("Thanks! Your message has been sent — we'll get back to you shortly.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Couldn't send your message. Please call or text us instead.");
    } finally {
      setSending(false);
    }
  };

  const cards = [
    { icon: Phone, t: "Call or Text", lines: [
      { text: "902-401-6212", href: "tel:+19024016212" },
      { text: "902-440-7855", href: "tel:+19024407855" },
    ] },
    { icon: Mail, t: "Email", lines: [
      { text: "info@addistaxiandtour.com", href: "mailto:info@addistaxiandtour.com" },
      { text: "addistaxibusiness@gmail.com", href: "mailto:addistaxibusiness@gmail.com" },
    ] },
    { icon: MapPin, t: "Location", lines: [{ text: "Halifax, Nova Scotia" }, { text: "Canada" }] },
    { icon: Clock, t: "Hours", lines: [{ text: "Open 24 / 7" }, { text: "365 days a year" }] },
  ];

  return (
    <div>
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pt-20 pb-12">
        <span className="text-sm uppercase tracking-[0.2em] text-gold">Contact</span>
        <h1 className="mt-3 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter">Get in touch</h1>
        <p className="mt-6 text-lg text-slate-300 max-w-2xl">Questions, custom trips, or corporate accounts — reach out any time. We're here 24/7.</p>
      </section>

      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pb-24 grid gap-10 lg:grid-cols-2">
        <div className="grid sm:grid-cols-2 gap-5">
          {cards.map((c, i) => (
            <motion.div key={c.t} {...fade(i * 0.06)} className="rounded-sm bg-navy-light border border-gold/20 p-7">
              <div className="grid place-items-center h-12 w-12 rounded-sm bg-gold/10 text-gold mb-4"><c.icon className="h-6 w-6" /></div>
              <h3 className="font-heading text-lg font-bold text-white mb-2">{c.t}</h3>
              {c.lines.map((l) => (
                l.href
                  ? <a key={l.text} href={l.href} data-testid={`contact-link-${l.text}`} className="block text-slate-300 hover:text-gold transition-colors duration-200">{l.text}</a>
                  : <p key={l.text} className="text-slate-300">{l.text}</p>
              ))}
            </motion.div>
          ))}
          <div className="sm:col-span-2 rounded-sm overflow-hidden border border-gold/20" data-testid="google-map">
            <iframe
              title="Bridge Taxi service area — Halifax, Nova Scotia"
              src="https://maps.google.com/maps?q=Halifax%2C%20Nova%20Scotia%2C%20Canada&t=&z=11&ie=UTF8&iwloc=&output=embed"
              className="w-full h-64 sm:h-72"
              style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <motion.form {...fade(0.1)} onSubmit={submit} className="rounded-sm bg-navy-light border border-gold/20 p-8" data-testid="contact-form">
          <h3 className="font-heading text-2xl font-bold text-white mb-6">Send a message</h3>
          <div className="space-y-4">
            <input data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
              className="w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold" />
            <input data-testid="contact-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email address"
              className="w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold" />
            <textarea data-testid="contact-message" rows={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="How can we help?"
              className="w-full rounded-sm bg-input border border-gold/20 px-4 py-3 text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-gold resize-none" />
          </div>
          <button type="submit" disabled={sending} data-testid="contact-submit" className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-3.5 font-semibold text-navy transition-transform duration-200 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-60">
            {sending ? <>Sending <Loader2 className="h-4 w-4 animate-spin" /></> : <>Send message <Send className="h-4 w-4" /></>}
          </button>
        </motion.form>
      </section>
    </div>
  );
}
